import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (err) {
      // /auth/me returns 401 when no session cookie is set — this is the
      // expected "logged-out" path on the public login pages. We swallow
      // because the consumer just renders the login form. Anything other
      // than 401 still ends up flagged via Axios interceptors.
      if (err?.response?.status !== 401 && typeof console !== "undefined") {
        // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
        console.warn("auth/me failed:", err?.message || err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const loginStaff = async (employee_code, pin) => {
    const { data } = await api.post("/auth/login-staff", { employee_code, pin });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout failure on the server is non-fatal — we still clear local
      // state so the user lands on the login screen. Most commonly this
      // happens when the session was already expired server-side.
      // eslint-disable-next-line no-console -- intentional dev/operator diagnostic
      if (typeof console !== "undefined") console.warn("Server logout failed (clearing client anyway):", err?.message || err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginStaff, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
